import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import type { DepartmentIntelligenceRow } from "@/api/intelligence";
import { formatCount, formatInr } from "@/lib/intelligenceFormat";
import { IntelligenceVariance } from "./IntelligenceVariance";
import { ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

type SortKey = keyof Pick<
	DepartmentIntelligenceRow,
	| "departmentName"
	| "doctorCount"
	| "revenue"
	| "patientVolume"
	| "opdVisits"
	| "ipdAdmissions"
	| "surgeries"
	| "revenueVariancePct"
	| "revenueContributionPct"
>;

type Props = {
	departments: DepartmentIntelligenceRow[];
	onSelectDepartment: (departmentId: string) => void;
	selectedDepartmentId?: string | null;
};

export function DepartmentPerformanceTable({
	departments,
	onSelectDepartment,
	selectedDepartmentId,
}: Props) {
	const [search, setSearch] = useState("");
	const [sortKey, setSortKey] = useState<SortKey>("revenue");
	const [sortAsc, setSortAsc] = useState(false);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		let rows = departments;
		if (q) {
			rows = rows.filter((d) => d.departmentName.toLowerCase().includes(q));
		}
		return [...rows].sort((a, b) => {
			const av = a[sortKey];
			const bv = b[sortKey];
			if (typeof av === "string" && typeof bv === "string") {
				return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
			}
			const na = Number(av ?? 0);
			const nb = Number(bv ?? 0);
			return sortAsc ? na - nb : nb - na;
		});
	}, [departments, search, sortKey, sortAsc]);

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) setSortAsc(!sortAsc);
		else {
			setSortKey(key);
			setSortAsc(false);
		}
	};

	const SortHead = ({ label, field }: { label: string; field: SortKey }) => (
		<TableHead
			className={cn("cursor-pointer whitespace-nowrap", field !== "departmentName" && "text-right")}
			onClick={() => toggleSort(field)}
		>
			<span className="inline-flex items-center gap-1">
				{label}
				<ArrowUpDown className="h-3 w-3 opacity-40" />
			</span>
		</TableHead>
	);

	return (
		<div className="space-y-3">
			<Input
				placeholder="Search department…"
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				className="max-w-md"
			/>
			<div className="rounded-lg border overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<SortHead label="Department" field="departmentName" />
							<SortHead label="Doctors" field="doctorCount" />
							<SortHead label="Patients" field="patientVolume" />
							<SortHead label="OPD" field="opdVisits" />
							<SortHead label="IPD" field="ipdAdmissions" />
							<SortHead label="Surgeries" field="surgeries" />
							<SortHead label="Revenue" field="revenue" />
							<TableHead className="text-right">Rev / Doctor</TableHead>
							<SortHead label="Share %" field="revenueContributionPct" />
							<SortHead label="Variance %" field="revenueVariancePct" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{filtered.length === 0 ? (
							<TableRow>
								<TableCell colSpan={10} className="text-center text-muted-foreground py-8">
									No departments with activity in this period
								</TableCell>
							</TableRow>
						) : (
							filtered.map((d) => (
								<TableRow
									key={d.departmentId}
									className={cn(
										"cursor-pointer",
										selectedDepartmentId === d.departmentId && "bg-indigo-50/60"
									)}
									onClick={() => onSelectDepartment(d.departmentId)}
								>
									<TableCell className="font-medium">{d.departmentName}</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.doctorCount)}</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.patientVolume)}</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.opdVisits)}</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.ipdAdmissions)}</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.surgeries)}</TableCell>
									<TableCell className="text-right tabular-nums font-medium">{formatInr(d.revenue)}</TableCell>
									<TableCell className="text-right tabular-nums text-sm">
										{d.revenuePerDoctor != null ? formatInr(d.revenuePerDoctor) : "—"}
									</TableCell>
									<TableCell className="text-right tabular-nums">
										{d.revenueContributionPct != null
											? `${d.revenueContributionPct.toFixed(1)}%`
											: "—"}
									</TableCell>
									<TableCell className="text-right">
										<IntelligenceVariance percent={d.revenueVariancePct} trend={d.trend} />
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
}
